
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { 
  BookOpen, 
  Code, 
  Rocket, 
  LucideIcon, 
  PenTool, 
  Users, 
  Briefcase, 
  Shield,
  DollarSign,
  FileText,
  Award,
  Zap,
  Search,
  Star,
  BrainCircuit,
  Clock,
  Hash,
  Globe,
  Layers,
  CheckCircle2,
  MessageCircle,
  Share2,
  Bookmark,
  TrendingUp,
  SlidersHorizontal
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
} from "@/components/ui/dropdown-menu";

interface TutorialCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  level: "Beginner" | "Intermediate" | "Advanced";
  duration: string;
  link: string;
  author?: string;
  popularity?: number;
  completed?: boolean;
  isNew?: boolean;
  category?: string;
}

const TutorialCard = ({ 
  icon: Icon, 
  title, 
  description, 
  level, 
  duration, 
  link, 
  author, 
  popularity, 
  completed, 
  isNew,
  category 
}: TutorialCardProps) => {
  const levelColors = {
    "Beginner": "bg-emerald-100 text-emerald-800",
    "Intermediate": "bg-blue-100 text-blue-800",
    "Advanced": "bg-purple-100 text-purple-800",
  };

  return (
    <motion.div 
      whileHover={{ y: -5 }}
      className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow"
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
            <Icon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold">{title}</h3>
              {isNew && (
                <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-200">
                  New
                </Badge>
              )}
            </div>
            <p className="text-gray-600 mb-4 text-sm">{description}</p>
            <div className="flex flex-col gap-3">
              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  <span className={`text-xs font-medium py-1 px-2 rounded-full ${levelColors[level]}`}>
                    {level}
                  </span>
                  <span className="text-xs font-medium bg-gray-100 text-gray-700 py-1 px-2 rounded-full flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {duration}
                  </span>
                  {category && (
                    <span className="text-xs font-medium bg-blue-50 text-blue-700 py-1 px-2 rounded-full flex items-center gap-1">
                      <Hash className="w-3 h-3" /> {category}
                    </span>
                  )}
                </div>
                {completed && (
                  <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Completed
                  </Badge>
                )}
              </div>
              
              <div className="flex justify-between items-center">
                {author && (
                  <div className="text-sm text-gray-500">
                    By {author}
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="text-gray-400 hover:text-gray-600">
                          <Bookmark className="w-4 h-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Save for later</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button className="text-gray-400 hover:text-gray-600">
                          <Share2 className="w-4 h-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Share tutorial</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  
                  <a 
                    href={link} 
                    className="text-blue-600 text-sm font-medium hover:text-blue-700 hover:underline"
                  >
                    Start Tutorial →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const featuredTutorials: TutorialCardProps[] = [
  {
    icon: PenTool,
    title: "Creating Your First Freelance Profile",
    description: "Learn how to build a compelling profile that attracts quality clients.",
    level: "Beginner",
    duration: "15 min",
    link: "#creating-profile",
    author: "Sarah Johnson",
    popularity: 4.8,
    isNew: true,
    category: "Profile",
  },
  {
    icon: Briefcase,
    title: "Writing Winning Proposals",
    description: "Master the art of proposal writing to stand out from the competition.",
    level: "Intermediate",
    duration: "25 min",
    link: "#writing-proposals",
    author: "Michael Chen",
    popularity: 4.7,
    category: "Business",
  },
  {
    icon: Code,
    title: "Blockchain Contract Integration",
    description: "Integrate smart contracts with your freelance projects for secure payment.",
    level: "Advanced",
    duration: "45 min",
    link: "#blockchain-integration",
    author: "Aisha Patel",
    popularity: 4.9,
    category: "Blockchain",
  },
  {
    icon: Users,
    title: "Building Your Client Network",
    description: "Strategies for building a sustainable client base for long-term success.",
    level: "Intermediate",
    duration: "20 min",
    link: "#client-network",
    author: "David Williams",
    popularity: 4.6,
    category: "Networking",
  },
  {
    icon: DollarSign,
    title: "Pricing Your Freelance Services",
    description: "Learn effective pricing strategies to maximize your earnings.",
    level: "Beginner",
    duration: "18 min",
    link: "#pricing-services",
    author: "Elena Rodriguez",
    popularity: 4.8,
    category: "Business",
  },
  {
    icon: Shield,
    title: "Securing Your Freelance Business",
    description: "Protect your work and payments with proper contracts and security measures.",
    level: "Intermediate",
    duration: "30 min",
    link: "#security-measures",
    author: "James Wilson",
    popularity: 4.7,
    category: "Security",
  },
];

const beginnerTutorials: TutorialCardProps[] = [
  {
    icon: BookOpen,
    title: "Getting Started with DGK Hub",
    description: "Learn the basics of navigating and using the DGK Freelance Hub platform.",
    level: "Beginner",
    duration: "10 min",
    link: "#getting-started",
    author: "Alex Morgan",
    completed: true,
    category: "Platform",
  },
  {
    icon: PenTool,
    title: "Creating Your First Freelance Profile",
    description: "Learn how to build a compelling profile that attracts quality clients.",
    level: "Beginner",
    duration: "15 min",
    link: "#creating-profile",
    author: "Sarah Johnson",
    isNew: true,
    category: "Profile",
  },
  {
    icon: DollarSign,
    title: "Pricing Your Freelance Services",
    description: "Learn effective pricing strategies to maximize your earnings.",
    level: "Beginner",
    duration: "18 min",
    link: "#pricing-services",
    author: "Elena Rodriguez",
    category: "Business",
  },
  {
    icon: FileText,
    title: "Creating Your First Contract",
    description: "Learn how to create and customize contracts for your freelance projects.",
    level: "Beginner",
    duration: "20 min",
    link: "#first-contract",
    author: "Thomas Brown",
    category: "Legal",
  },
  {
    icon: Globe,
    title: "Working Across Time Zones",
    description: "Master the challenges of working with international clients in different time zones.",
    level: "Beginner",
    duration: "12 min",
    link: "#time-zones",
    author: "Sophia Lee",
    isNew: true,
    category: "Global Work",
  },
  {
    icon: MessageCircle,
    title: "Effective Client Communication",
    description: "Learn communication strategies that build trust and prevent misunderstandings.",
    level: "Beginner",
    duration: "22 min",
    link: "#client-communication",
    author: "Ryan Peters",
    category: "Communication",
  },
];

const intermediateTutorials: TutorialCardProps[] = [
  {
    icon: Briefcase,
    title: "Writing Winning Proposals",
    description: "Master the art of proposal writing to stand out from the competition.",
    level: "Intermediate",
    duration: "25 min",
    link: "#writing-proposals",
    author: "Michael Chen",
    category: "Business",
  },
  {
    icon: Users,
    title: "Building Your Client Network",
    description: "Strategies for building a sustainable client base for long-term success.",
    level: "Intermediate",
    duration: "20 min",
    link: "#client-network",
    author: "David Williams",
    category: "Networking",
  },
  {
    icon: Shield,
    title: "Securing Your Freelance Business",
    description: "Protect your work and payments with proper contracts and security measures.",
    level: "Intermediate",
    duration: "30 min",
    link: "#security-measures",
    author: "James Wilson",
    category: "Security",
  },
  {
    icon: Award,
    title: "Earning Platform Badges",
    description: "Learn how to earn recognition badges to boost your profile visibility.",
    level: "Intermediate",
    duration: "15 min",
    link: "#earning-badges",
    author: "Natalie Kim",
    category: "Profile",
  },
  {
    icon: TrendingUp,
    title: "Scaling Your Client Work",
    description: "Techniques for handling multiple clients and projects simultaneously.",
    level: "Intermediate",
    duration: "28 min",
    link: "#scaling-work",
    author: "Omar Hassan",
    isNew: true,
    category: "Business Growth",
  },
  {
    icon: Layers,
    title: "Creating a Professional Portfolio",
    description: "Showcase your work effectively to attract higher-paying clients.",
    level: "Intermediate",
    duration: "24 min",
    link: "#portfolio-creation",
    author: "Isabella Martinez",
    category: "Portfolio",
  },
];

const advancedTutorials: TutorialCardProps[] = [
  {
    icon: Code,
    title: "Blockchain Contract Integration",
    description: "Integrate smart contracts with your freelance projects for secure payment.",
    level: "Advanced",
    duration: "45 min",
    link: "#blockchain-integration",
    author: "Aisha Patel",
    category: "Blockchain",
  },
  {
    icon: Rocket,
    title: "Scaling Your Freelance Business",
    description: "Strategies for growing from solo freelancer to agency owner.",
    level: "Advanced",
    duration: "40 min",
    link: "#scaling-business",
    author: "Robert Chen",
    category: "Business Growth",
  },
  {
    icon: Zap,
    title: "Leveraging AI Tools for Productivity",
    description: "Use AI tools to automate tasks and increase your freelance productivity.",
    level: "Advanced",
    duration: "35 min",
    link: "#ai-tools",
    author: "Priya Singh",
    isNew: true,
    category: "Technology",
  },
  {
    icon: BrainCircuit,
    title: "Advanced Negotiation Techniques",
    description: "Psychological approaches to negotiate better rates and terms with clients.",
    level: "Advanced",
    duration: "38 min",
    link: "#negotiation",
    author: "Daniel Johnson",
    category: "Business",
  },
  {
    icon: Star,
    title: "Becoming a Top-Rated Freelancer",
    description: "Strategic approaches to earn top-rated status and premium clients.",
    level: "Advanced",
    duration: "42 min",
    link: "#top-rated",
    author: "Maria Garcia",
    category: "Career Growth",
  },
];

// New specialized categories
const blockchainTutorials: TutorialCardProps[] = [
  {
    icon: Code,
    title: "Blockchain Contract Integration",
    description: "Integrate smart contracts with your freelance projects for secure payment.",
    level: "Advanced",
    duration: "45 min",
    link: "#blockchain-integration",
    author: "Aisha Patel",
    category: "Blockchain",
  },
  {
    icon: Shield,
    title: "Securing Cryptocurrency Payments",
    description: "Best practices for accepting and managing cryptocurrency payments.",
    level: "Intermediate",
    duration: "32 min",
    link: "#crypto-payments",
    author: "Wei Zhang",
    category: "Blockchain",
  },
  {
    icon: Code,
    title: "Smart Contract Essentials",
    description: "Learn the fundamentals of smart contracts for freelance agreements.",
    level: "Intermediate",
    duration: "38 min",
    link: "#smart-contracts",
    author: "Raj Kumar",
    category: "Blockchain",
  },
];

const Tutorials = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [completedTutorials, setCompletedTutorials] = useState<string[]>([]);
  
  const allCategories = [
    "All",
    "Profile",
    "Business",
    "Blockchain",
    "Networking",
    "Security",
    "Legal",
    "Global Work",
    "Communication",
    "Portfolio",
    "Technology",
    "Career Growth",
    "Business Growth",
  ];
  
  const filterTutorials = (tutorials: TutorialCardProps[]) => {
    return tutorials.filter(tutorial => {
      // Filter by search query
      const matchesSearch = tutorial.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                            tutorial.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            (tutorial.author && tutorial.author.toLowerCase().includes(searchQuery.toLowerCase())) ||
                            (tutorial.category && tutorial.category.toLowerCase().includes(searchQuery.toLowerCase()));
      
      // Filter by category
      const matchesCategory = selectedCategories.length === 0 || 
                              selectedCategories.includes("All") ||
                              (tutorial.category && selectedCategories.includes(tutorial.category));
      
      return matchesSearch && matchesCategory;
    });
  };
  
  const handleCategoryToggle = (category: string) => {
    if (category === "All") {
      setSelectedCategories(selectedCategories.includes("All") ? [] : ["All"]);
      return;
    }
    
    setSelectedCategories(prev => {
      // Remove "All" if it was selected and we're selecting a specific category
      const withoutAll = prev.filter(c => c !== "All");
      
      if (prev.includes(category)) {
        return withoutAll.filter(c => c !== category);
      } else {
        return [...withoutAll, category];
      }
    });
  };
  
  const markAsCompleted = (tutorialTitle: string) => {
    setCompletedTutorials(prev => {
      if (prev.includes(tutorialTitle)) {
        return prev.filter(title => title !== tutorialTitle);
      } else {
        return [...prev, tutorialTitle];
      }
    });
  };
  
  // Add completion status to tutorials
  const addCompletionStatus = (tutorials: TutorialCardProps[]) => {
    return tutorials.map(tutorial => ({
      ...tutorial,
      completed: completedTutorials.includes(tutorial.title)
    }));
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12">
      <Helmet>
        <title>Tutorials | DGK Freelance Hub</title>
      </Helmet>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent">
            Tutorials & Learning Resources
          </h1>
          <p className="text-gray-600 max-w-3xl mx-auto">
            Enhance your freelancing skills with our comprehensive tutorials. 
            Whether you're just starting out or looking to advance your career, 
            we have resources for every skill level.
          </p>
        </div>
        
        {/* Featured Tutorial Hero */}
        <div className="relative bg-gradient-to-r from-blue-600 to-emerald-600 rounded-2xl p-8 mb-12 overflow-hidden">
          <div className="absolute inset-0 bg-opacity-10 bg-white">
            <div className="absolute inset-0 opacity-10 bg-grid-pattern"></div>
          </div>
          <div className="relative z-10 flex flex-col lg:flex-row items-center gap-8">
            <div className="flex-1">
              <Badge className="bg-white/20 text-white mb-4 backdrop-blur-sm">Featured Series</Badge>
              <h2 className="text-3xl font-bold text-white mb-4">Blockchain for Freelancers</h2>
              <p className="text-white/90 mb-6">
                Master cryptocurrency payments, smart contracts, and blockchain security to offer cutting-edge services to your clients. This comprehensive series takes you from the basics to advanced implementation.
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                {blockchainTutorials.map((tutorial, index) => (
                  <Badge key={index} variant="outline" className="bg-white/10 text-white border-white/20 backdrop-blur-sm">
                    {tutorial.title}
                  </Badge>
                ))}
              </div>
              <Button className="bg-white text-blue-600 hover:bg-blue-50">
                Start the Series
              </Button>
            </div>
            <div className="w-full lg:w-2/5 h-64 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center">
              <Code className="w-24 h-24 text-white opacity-80" />
            </div>
          </div>
        </div>
        
        {/* Search and Filters */}
        <div className="mb-12">
          <div className="bg-white shadow-sm border rounded-xl p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  className="pl-10"
                  placeholder="Search tutorials, topics, or authors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-3">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="flex items-center gap-2">
                      <SlidersHorizontal className="h-4 w-4" /> 
                      {selectedCategories.length > 0 
                        ? `${selectedCategories.length} categories selected` 
                        : "Filter by category"}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56">
                    {allCategories.map((category) => (
                      <DropdownMenuCheckboxItem
                        key={category}
                        checked={selectedCategories.includes(category)}
                        onCheckedChange={() => handleCategoryToggle(category)}
                      >
                        {category}
                      </DropdownMenuCheckboxItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-16">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">Featured Tutorials</h2>
            <Button variant="outline">View All Featured</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filterTutorials(addCompletionStatus(featuredTutorials)).slice(0, 3).map((tutorial, index) => (
              <TutorialCard key={index} {...tutorial} />
            ))}
          </div>
        </div>
        
        <Tabs defaultValue="all" className="mb-16">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">Browse by Level</h2>
            <TabsList>
              <TabsTrigger value="all">All Levels</TabsTrigger>
              <TabsTrigger value="beginner">Beginner</TabsTrigger>
              <TabsTrigger value="intermediate">Intermediate</TabsTrigger>
              <TabsTrigger value="advanced">Advanced</TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="all">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filterTutorials(addCompletionStatus([
                ...beginnerTutorials,
                ...intermediateTutorials, 
                ...advancedTutorials
              ])).slice(0, 6).map((tutorial, index) => (
                <TutorialCard key={index} {...tutorial} />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="beginner">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filterTutorials(addCompletionStatus(beginnerTutorials)).map((tutorial, index) => (
                <TutorialCard key={index} {...tutorial} />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="intermediate">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filterTutorials(addCompletionStatus(intermediateTutorials)).map((tutorial, index) => (
                <TutorialCard key={index} {...tutorial} />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="advanced">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filterTutorials(addCompletionStatus(advancedTutorials)).map((tutorial, index) => (
                <TutorialCard key={index} {...tutorial} />
              ))}
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Learning Paths Section */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">Learning Paths</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="h-2 bg-emerald-500"></div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Freelancer Essentials</h3>
                <p className="text-gray-600 mb-4">Master the core skills needed to launch your freelance career successfully.</p>
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>6 tutorials</span>
                  <span>3 hours total</span>
                </div>
                <Button className="w-full">Start Path</Button>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="h-2 bg-blue-500"></div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Business Growth</h3>
                <p className="text-gray-600 mb-4">Scale your freelance operations and increase your revenue.</p>
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>8 tutorials</span>
                  <span>4.5 hours total</span>
                </div>
                <Button className="w-full">Start Path</Button>
              </div>
            </div>
            
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="h-2 bg-purple-500"></div>
              <div className="p-6">
                <h3 className="text-xl font-semibold mb-3">Blockchain Expert</h3>
                <p className="text-gray-600 mb-4">Become an expert in blockchain technologies for secure freelance work.</p>
                <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
                  <span>5 tutorials</span>
                  <span>3.5 hours total</span>
                </div>
                <Button className="w-full">Start Path</Button>
              </div>
            </div>
          </div>
        </div>
        
        {/* New this Month */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">New This Month</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filterTutorials(addCompletionStatus([
              ...beginnerTutorials,
              ...intermediateTutorials,
              ...advancedTutorials
            ]))
              .filter(tutorial => tutorial.isNew)
              .map((tutorial, index) => (
                <TutorialCard key={index} {...tutorial} />
              ))}
          </div>
        </div>
        
        {/* Special Section for Blockchain */}
        <div className="mb-16">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">Blockchain Tutorials</h2>
            <Button variant="outline">View All Blockchain</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filterTutorials(addCompletionStatus(blockchainTutorials)).map((tutorial, index) => (
              <TutorialCard key={index} {...tutorial} />
            ))}
          </div>
        </div>
        
        {/* CTA Section */}
        <div className="bg-gradient-to-r from-blue-50 to-emerald-50 rounded-2xl p-8 mb-16">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">Can't find what you're looking for?</h2>
            <p className="text-gray-600 mb-6">
              Our team is constantly creating new tutorials based on community feedback. 
              Let us know what topics you'd like to see covered.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#request-tutorial" 
                className="bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                Request a Tutorial
              </a>
              <a 
                href="#support-center" 
                className="bg-white text-gray-700 border border-gray-300 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Visit Support Center
              </a>
            </div>
          </div>
        </div>
        
        {/* Community Contributors */}
        <div className="mb-16">
          <h2 className="text-2xl font-semibold mb-6">Top Tutorial Contributors</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: "Sarah Johnson", role: "Profile Expert", tutorials: 8, image: "https://randomuser.me/api/portraits/women/44.jpg" },
              { name: "Michael Chen", role: "Business Growth", tutorials: 12, image: "https://randomuser.me/api/portraits/men/32.jpg" },
              { name: "Aisha Patel", role: "Blockchain Specialist", tutorials: 6, image: "https://randomuser.me/api/portraits/women/68.jpg" },
              { name: "David Williams", role: "Client Relations", tutorials: 9, image: "https://randomuser.me/api/portraits/men/75.jpg" }
            ].map((contributor, index) => (
              <div key={index} className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow p-6 text-center">
                <div className="w-20 h-20 rounded-full mx-auto mb-4 overflow-hidden">
                  <img src={contributor.image} alt={contributor.name} className="w-full h-full object-cover" />
                </div>
                <h3 className="font-semibold text-lg">{contributor.name}</h3>
                <p className="text-gray-500 text-sm mb-2">{contributor.role}</p>
                <p className="text-blue-600 font-medium">{contributor.tutorials} tutorials</p>
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Tutorials;
